import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router";
import {useTranslation} from "react-i18next";

import circleRingIcon from "url:shared-assets/icons/circle-rings.svg";
import circleRingIconWhite from "url:shared-assets/icons/white/circle-rings.svg";
import teamIcon from "url:shared-assets/icons/team.svg";
import teamIconWhite from "url:shared-assets/icons/white/team.svg";
import videoCameraIcon from "url:shared-assets/icons/gray/video-camera.svg";
import videoCameraIconWhite from "url:shared-assets/icons/white/video-camera.svg";
import insightsIconWhite from "url:shared-assets/icons/white/insights.svg";
import insightsIcon from "url:shared-assets/icons/insights.svg";
import calendarIcon from "url:shared-assets/icons/calendar-2.svg";
import calendarIconWhite from "url:shared-assets/icons/white/calendar-2.svg";
import {getLanguage} from "../../lang/helpers";
import {ThreadInterface} from "../../interfaces/common";
import Api from "../../services/Api";
import {Modal, ModalHeader, ModalBody, ModalFooter} from "../Modal";
import DotsPopup from "../Popup/DotsPopup";
import CustomButton from "../CustomButton"
import iconPencilBare from "url:shared-assets/icons/pencil-bare.svg";
import iconTrash from "url:shared-assets/icons/trash.svg";
import iconClients from "url:shared-assets/icons/clients.svg";
import iconClientsWhite from "url:shared-assets/icons/white/clients.svg";
import iconChat from "url:shared-assets/icons/chat.svg";
import iconChatWhite from "url:shared-assets/icons/white/chat.svg";
import iconAvailability from "url:shared-assets/icons/availability.svg";
import iconAvailabilityWhite from "url:shared-assets/icons/white/availability.svg";
import dotsVerticalIcon from "url:shared-assets/icons/dots-vertical.svg";
import {useAtom} from "jotai";
import {Home, Brain} from "flowbite-react-icons/outline";
import {
  threadsAtom, customerCategoryAtom, unreadChatsAtom, departmentAtom, onboardedAtom,
  customerSurveysAtom
} from "../../jotai_state";
import {CONFIG} from "../../constants";

interface MenuItem {
  title: string;
  link: string;
  icon: React.ReactNode;
  badge?: boolean;
  selected?: boolean;
}

interface Props {
  menuClick?: Function;
  showSidebar: boolean;
}

const expandedMinWidth = 925;

const NavMenu: React.FC<Props> = (props) => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const translation = useTranslation();
  const [showDeleteThread, setShowDeleteThread] = useState<string>("");
  const [showRenameThread, setShowRenameThread] = useState<string>("");
  const [threadName, setThreadName] = useState<string>("");
  const {t, i18n} = translation;
  const lang = getLanguage(i18n.language);
  const midnight = new Date().setHours(23, 59, 59);
  const secondsPerDay = 24 * 3600;
  const [threads, setThreads] = useAtom(threadsAtom);
  const [customerCategories] = useAtom(customerCategoryAtom);
  const [unreadChats] = useAtom(unreadChatsAtom);
  const [department] = useAtom(departmentAtom);
  const [onboarded] = useAtom(onboardedAtom);
  const [customerSurveys] = useAtom(customerSurveysAtom);
  const scrollRef = useRef<HTMLDivElement>(null);
  const userType = CONFIG.USER_TYPE;

  //  collapsed by default (w-16), overlay fixed
  //  on hover expand to w-64 and reveal labels + thread list
  const [expanded, setExpanded] = useState(window.innerWidth < expandedMinWidth);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < expandedMinWidth) {
        setExpanded(true); // always expanded on mobile
      } else {
        setExpanded(false); // default collapsed on larger screens
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (props.showSidebar) {
      if (scrollRef.current) {
        scrollRef.current.scrollTo(0, 0);
      }
    }
  }, [props.showSidebar])

  useEffect(() => {
    if (!location.pathname.startsWith("/ai/")) {
      const _threads = [...threads];
      for (const _thread of _threads) {
        _thread.selected = false;
      }
      setThreads(_threads);
    }
  }, [location.pathname]);

  const goTo = (link: string) => {
    const _threads = [...threads];
    for (const _thread of _threads) {
      _thread.selected = false;
    }
    if (props.menuClick) {
      props.menuClick();
    }
    navigate(link);
  };

  const selectThread = (threadId: string) => {
    const _threads = [...threads];
    for (const _thread of _threads) {
      _thread.selected = _thread.id === threadId;
    }
    setThreads(_threads);
    if (props.menuClick) {
      props.menuClick();
    }
    navigate("/ai/" + threadId);
  };

  const calculateMenuItems = () => {
    let items: Array<MenuItem> = [];
    const p = window.location.pathname;
    let selected = false;

    if (userType === "coach") {
      selected = p === "/"
      items.push({
        title: t("start"),
        link: "/",
        icon: <Home style={{color: selected ? "#ffffff" : ""}} className="h-6 w-6"/>,
        selected: selected,
      });
      if (onboarded) {
        selected = p === "/bookings";
        items.push({
          title: t("my-bookings"),
          link: "/bookings",
          icon: (
            <img
              src={selected ? calendarIconWhite : calendarIcon}
              className="h-6 w-6"
              alt="home"
            />
          ),
          selected: selected,
        });
        selected = p.startsWith("/clients")
        items.push({
          title: t("my-clients"),
          link: "/clients",
          icon: (
            <img
              src={selected ? iconClientsWhite : iconClients}
              className="h-6 w-6"
              alt="home"
            />
          ),
          selected: selected,
        });
        selected = p.startsWith("/chats");
        items.push({
          title: t("chat"),
          link: "/chats",
          icon: (
            <img
              src={selected ? iconChatWhite : iconChat}
              className="h-6 w-6"
              alt="home"
            />
          ),
          selected: selected,
        });

        selected = p === "/availability"
        items.push({
          title: t("availability"),
          link: "/availability",
          icon: (
            <img
              src={selected ? iconAvailabilityWhite : iconAvailability}
              className="h-6 w-6"
              alt="home"
            />
          ),
          selected: selected,
        });
      }
    } else {
      selected = p === "/"
      items.push({
        title: t("start"),
        link: "/",
        icon: <Home style={{color: selected ? "#ffffff" : ""}} className="h-6 w-6"/>,
        selected: selected,
      });

      if (customerCategories.length > 0) {
        selected = (p.startsWith("/dev-plan") && !p.startsWith("/dev-plan/survey/result"));
        items.push({
          title: t("long-term-goals"),
          link: "/dev-plan",
          icon: (
            <img
              src={
                selected ? circleRingIconWhite : circleRingIcon
              }
              className="h-6 w-6"
              alt="development plan"
            />
          ),
          selected: selected,
        });

        if (customerSurveys > 0) {
          selected = (p.startsWith("/assessments") || p.startsWith("/dev-plan/survey/result"));
          items.push({
            title: t("assessments"),
            link: "/assessments",
            icon: (
              <img
                src={
                  selected ? insightsIconWhite : insightsIcon
                }
                className="h-6 w-6"
                alt="Assessments"
              />
            ),
            selected: selected,
          });
        }
      }

      selected = p.startsWith("/profile")
      items.push({
        title: lang === "sv" ? "Profil" : "Profile",
        link: "/profile",
        icon: <Brain style={{color: selected ? "#ffffff" : ""}}
                     className={"h-6 w-6 " + (selected ? "text-neutral-100" : "")}/>,
        selected: selected,
      });

      if (department) {
        selected = p.startsWith("/team");
        items.push({
          title: "Team",
          link: "/team",
          icon: (
            <img
              src={
                selected ? teamIconWhite : teamIcon
              }
              className="h-6 w-6"
              alt="team"
            />
          ),
          selected: selected,
        });
      }


      selected = p.startsWith("/videocoach")
      items.push({
        title: t("video-coach-nav-title"),
        link: "/videocoach",
        icon: (
          <img
            src={
              selected ? videoCameraIconWhite : videoCameraIcon
            }
            className="h-6 w-6"
            alt="videocoach"
          />
        ),
        badge: unreadChats > 0,
        selected: selected,
      });
    }

    return items;
  };

  const combinedMenuItems = calculateMenuItems();

  const getDateName = (d: string) => {
    const secondsToMidnight = (midnight - new Date(d).getTime()) / 1000;

    if (secondsToMidnight < secondsPerDay) {
      return lang === "sv" ? "Idag" : "Today";
    } else if (secondsToMidnight < 2 * secondsPerDay) {
      return lang === "sv" ? "Igår" : "Yesterday";
    } else if (secondsToMidnight < 8 * secondsPerDay) {
      return lang === "sv" ? "Senaste sju dagarna" : "Last seven days";
    } else if (secondsToMidnight < 31 * secondsPerDay) {
      return lang === "sv" ? "Senaste månaden" : "Last month";
    }
    return lang === "sv" ? "Äldre" : "Older";
  };

  const showThreadName = (thread: ThreadInterface): string => {
    let name: string;
    if (thread.topic === "New topic") {
      name = lang === "sv" ? "Nytt ämne" : "New topic";
    } else {
      name = thread.topic;
    }

    return name;
  };

  const renameThread = () => {
    const data = {
      name: threadName,
      threadId: showRenameThread,
    };

    api.post("client/chat_thread_rename", data).then((response) => {
      if (response.json.status === "ok") {
        const _threads: Array<ThreadInterface> = [];
        for (const _thread of threads) {
          if (_thread.id === showRenameThread) {
            _thread.topic = threadName;
          }
          _threads.push(_thread);
        }
        setShowRenameThread("");
        setThreads(_threads);
      }
    });
  };

  const deleteThread = (_threadId: string) => {
    api.get("client/chat_thread_remove/" + _threadId).then((response) => {
      if (response.json.status === "ok") {
        setShowDeleteThread("");
        const _threads: Array<ThreadInterface> = [];
        for (const _thread of threads) {
          if (_thread.id !== _threadId) {
            _threads.push(_thread);
          }
        }
        setThreads(_threads);
        if (location.pathname === "/ai/" + _threadId) {
          navigate("/ai/0");
        }
      }
    });
  };

  const getThreadName = (_threadId: string) => {
    for (const thread of threads) {
      if (thread.id === _threadId) {
        return thread.topic;
      }
    }
    return "?";
  };

  const renderModals = () => {
    if (showDeleteThread !== "") {
      return (
        <Modal isOpen={true} onClose={() => setShowDeleteThread("")}>
          <ModalHeader onClose={() => setShowDeleteThread("")}>
            {lang === "sv" ? "Radera session?" : "Delete session?"}
          </ModalHeader>
          <ModalBody>
            <div className="text-gray-400">
              {lang === "sv" ? "Ta bort " : "This will delete "}
              {getThreadName(showDeleteThread)}
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="w-full">
              <CustomButton
                buttonType="outlineWhiteBase"
                onClick={() => setShowDeleteThread("")}
              >
                {t("cancel")}
              </CustomButton>
              <CustomButton
                buttonType="dangerBase"
                className="mt-2"
                onClick={() => deleteThread(showDeleteThread)}
              >
                {t("delete")}
              </CustomButton>
            </div>
          </ModalFooter>
        </Modal>
      );
    } else if (showRenameThread !== "") {
      return (
        <Modal isOpen={true} onClose={() => setShowRenameThread("")}>
          <ModalHeader onClose={() => setShowRenameThread("")}>
            {lang === "sv" ? "Byt namn på session" : "Rename session"}
          </ModalHeader>
          <ModalBody>
            <input
              type="text"
              value={threadName}
              className="z-input z-bg-unset"
              onChange={(e) => setThreadName(e.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <div className="w-full">
              <CustomButton
                buttonType="outlineWhiteBase"
                onClick={() => setShowRenameThread("")}
              >
                {t("cancel")}
              </CustomButton>
              <CustomButton
                buttonType="blueBase"
                className="mt-2"
                onClick={() => renameThread()}
              >
                {t("save")}
              </CustomButton>
            </div>
          </ModalFooter>
        </Modal>
      );
    }
    return null;
  };

  const renderThreadNames = () => {
    let previousDateName = "";
    return (
      <>
        {threads.map((thread, index) => {
          let dateName = getDateName(thread.updated);
          let _threadName = showThreadName(thread);
          let showName = "";
          if (dateName !== previousDateName) {
            showName = dateName;
          }
          previousDateName = dateName;
          let cls = "";
          if (thread.selected) {
            cls = " bg-indigo-900";
          }
          let popItems = [
            {
              title: lang === "sv" ? "Byt namn" : "Rename",
              click: () => {
                setShowRenameThread(thread.id);
                setThreadName(_threadName);
              },
              img: <img src={iconPencilBare} alt="edit" className="h-6 w-6"/>,
            },
            {
              title: t("delete"),
              click: () => setShowDeleteThread(thread.id),
              img: <img src={iconTrash} alt="delete" className="h-6 w-6"/>,
            },
          ];
          return (
            <div key={"thread_" + index}>
              {showName.length > 0 && (
                <div className="mt-6 mb-6 z-title4">{showName}</div>
              )}
              <div
                className={
                  "group p-2 rounded-md flex items-center cursor-pointer hover:bg-indigo-800 justify-between" +
                  cls
                }
                key={"thread_" + index}
              >
                <div
                  className="font-medium cursor-pointer w-full"
                  onClick={() => selectThread(thread.id)}
                >
                  <div className="text-sm" title={thread.summary}>{_threadName}</div>
                  <div className="text-xs text-gray-400">
                    {thread.name
                      ? thread.name
                      : lang === "sv"
                        ? "Eget ämne"
                        : "Own topic"}
                  </div>
                </div>
                <div
                  className={
                    thread.selected ? "" : "invisible group-hover:visible"
                  }
                >
                  <DotsPopup items={popItems} noHover={true}/>
                </div>
              </div>
            </div>
          );
        })}
      </>
    );
  };


  // Render: collapsed overlay sidebar (w-16) and hover expansion -> w-64
  return (
    <>
      <div
        onMouseEnter={() => {
          if (window.innerWidth >= expandedMinWidth) setExpanded(true);
        }}
        onMouseLeave={() => {
          if (window.innerWidth >= expandedMinWidth) setExpanded(false);
        }}
        className={
          "z-navmenu fixed gap-4 left-0 top-[82px] bg-indigo-1000 z-50 text-white flex flex-col"
        }
        style={{
          width: window.innerWidth > expandedMinWidth ? expanded ? "16rem" : "4rem" : "100%",
          // height: window.innerWidth > expandedMinWidth ? "100%" : undefined,
          height: "100%",
          transition: "width 200ms ease",
          backgroundColor: window.innerWidth < expandedMinWidth ? "#19193C" : undefined, // mobile color
        }}
        aria-expanded={expanded}

      >
        <div ref={scrollRef} className="flex flex-col py-3 overflow-y-auto">
          <div className="flex flex-col gap-2 px-3">
            {combinedMenuItems.map((item, i) => (
              <div
                key={"item_" + i}
                onClick={() => goTo(item.link)}

                // when collapsed provide title tooltip for accessibility
                title={!expanded ? item.title : undefined}
                className={
                  "relative flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors duration-150 " +
                  (item.selected
                    ? "bg-indigo-900 text-white"
                    : "text-gray-400 hover:bg-indigo-800")
                }
                aria-label={item.title}
                role="button"
              >
                <div className="h-6 w-6 flex items-center justify-center shrink-0">
                  {item.icon}
                </div>

                {/* Label: only visible when expanded */}
                <div
                  className={
                    "flex-1 overflow-hidden transition-all duration-200 ease-in-out " +
                    (expanded ? "opacity-100 max-w-48" : "opacity-0 max-w-0")
                  }
                  style={{whiteSpace: "nowrap"}}
                >
                  <span className="z-title2 select-none">{item.title}</span>
                </div>

                {item.badge && (
                  <div
                    className={
                      "ml-auto rounded-full bg-cherry-500 h-2 w-2 transition-opacity duration-200"
                    }
                    style={{position: "absolute", top: 10, left: 8}}
                  />
                )}
              </div>
            ))}
          </div>

          {
            userType === "client" &&
              <>
                  <div
                      className={
                        "border-t border-indigo-800 mt-4 mx-2 transition-opacity duration-200 " +
                        (expanded ? "opacity-100" : "opacity-60")
                      }
                  />
                {/* Add dots-vertical icon for collapsed sidebar */}
                {!expanded && (
                  <div className="flex items-center justify-center mt-4 px-3 py-2">
                    <img
                      src={dotsVerticalIcon}
                      alt="dots vertical"
                      className="h-6 w-6 shrink-0"
                      style={{height: "24px", width: "24px"}}
                    />
                  </div>
                )}

                {/* Threads: only render when expanded */}
                  <div
                      className={
                        "px-3 text-sm transition-opacity duration-200  " +
                        (expanded ? "opacity-100" : "opacity-0 pointer-events-none")
                      }
                      style={{flex: 1}}
                  >
                    {expanded ? renderThreadNames() : null}
                  </div>
                  <div>&nbsp;</div>
                  <div>&nbsp;</div>

              </>
          }
        </div>

      </div>

      {renderModals()}
    </>
  );
};

export default NavMenu;